import PlaylistEventsManager from "./PlaylistEventsManager";

import {
  audioTypeFromPath,
  cacheBestEffort,
  cachedBlob,
  evictBestEffort,
  safeUrlForLog,
} from "./PlaylistCache";

import FilesService from "../../infrastructure/Services/FilesService";

/** Fuentes que hay que bajar a un blob antes de poder reproducirlas. */
const REMOTE_SOURCE = /^https?:\/\//i;

/**
 * Tope para la bajada de una fuente remota. Sin esto un host que acepta la
 * conexión y no contesta deja `play()` colgado para siempre y, como `play()`
 * es parte de la cadena de rotación, eso es aire muerto en emisión. Solo pesa
 * la primera vez: después la pista sale del caché.
 *
 * Es un tope de INACTIVIDAD, no de bajada completa: se rearma con cada chunk
 * que llega. Como deadline de transferencia entera era incompatible con el
 * tope de tamaño de acá abajo —llenar 128 MiB en 20 s pide ~54 Mbps
 * sostenidos, y hasta el MP3 de 320 kbps y 8 min que justifica ese tope
 * (~19 MB) necesita ≥8 Mbps—, así que en un enlace modesto una pista SANA se
 * abortaba a los 20 s, se reportaba como "sin camino de reproducción" y se
 * salteaba. Y para siempre: el blob solo se cachea cuando la reproducción lo
 * valida, así que cada vuelta de la rotación repetía la misma bajada abortada
 * hasta cerrar la lista con `PlaylistEmpty`. Lo que hay que cortar es un host
 * que acepta la conexión y NO MANDA NADA: una condición de inactividad.
 */
const REMOTE_FETCH_TIMEOUT_MS = 20_000;

/**
 * Tope de tamaño para una fuente remota, aplicado MIENTRAS se lee el cuerpo.
 * `PlaylistManager` acepta cualquier `http(s)` persistida, así que del otro
 * lado puede haber una respuesta enorme o directamente sin fin (un stream de
 * radio, un endpoint mal apuntado): juntarla entera en un blob agota la
 * memoria del renderer —que es el mismo proceso que compone la emisión— y la
 * cuota del caché, y deja la playlist clavada.
 *
 * 128 MiB es holgado para audio y corto para un desastre: una pista de 8 min
 * en MP3 320 kbps pesa ~19 MB, una de 12 min en WAV sin comprimir (1411 kbps)
 * ~127 MB, y un set entero de 55 min en 320 kbps ~127 MB. Todo lo que se pasa
 * de eso no es una canción de la playlist.
 */
const REMOTE_MAX_BYTES = 128 * 1024 * 1024;

export interface TrackMetadata {
  title?: string;

  artist?: string;

  album?: string;

  genre?: string[];

  year?: number;

  bitrate?: number;

  duration?: number;
}

export interface IProgressPlaylist {
  currentTime: number;

  duration: number;

  progress: number;
}

class PlaylistTrack {
  private AudioContext: AudioContext;

  private MediaStreamDestination: MediaStreamAudioDestinationNode;

  private AudioSource: string | Blob;

  private AudioElement: HTMLAudioElement | null = null;

  private ObjectUrl: string | null = null;

  private SourceNode: MediaElementAudioSourceNode | null = null;

  private GainNode: GainNode | null = null;

  private AnalyserNode: AnalyserNode | null = null;

  private Metadata: TrackMetadata = {};

  /**
   * Instancia descartada: `destroy()` la marca y ya no puede volver a tocar el
   * audio. Es la única memoria que sobrevive a un `await`, y por eso la usan
   * todos los puntos de `play()` que retoman después de uno: mientras se baja
   * una fuente remota, otra pista puede pisar a ésta, y sin esta marca el
   * `fetch` que resolvía tarde creaba y arrancaba su <audio> con OTRO control
   * ya instalado — dos pistas sonando mezcladas y la vieja sin callback
   * alcanzable.
   */
  private Cancelled = false;

  /** Bajada remota en vuelo, para poder cortarla desde `destroy()`. */
  private RemoteFetch: AbortController | null = null;

  /**
   * Pausa pedida mientras la pista TODAVÍA no tenía <audio> (se estaba
   * bajando). `pauseTrack()` volvía en seco en ese estado —`AudioElement` es
   * null— y la continuación de `play()` creaba y ARRANCABA el audio después de
   * la pausa: el usuario paraba la música y la pista empezaba sola unos
   * segundos más tarde, con el transporte de la UI en "pausado".
   *
   * No se cancela la bajada: los bytes ya pedidos sirven igual y el `resume()`
   * arranca al toque. Lo que se marca es que ese arranque pendiente NO suene.
   */
  private PauseRequested = false;

  /**
   * Arranque EN VUELO, publicado mientras dura. Un `resumeTrack()` que llega
   * cuando todavía no hay <audio> no tiene nada que reanudar por su cuenta —la
   * pista se está bajando— y su resultado es el de ESTE intento: sin él, el
   * reanudar contestaba "sonó" con el <audio> todavía inexistente.
   */
  private PlayAttempt: Promise<boolean> | null = null;

  /**
   * Bytes ya bajados esperando a que la reproducción pruebe que se pueden
   * DECODIFICAR. Guardarlos antes fijaba en el caché —bajo la URL remota— una
   * respuesta 200 que es una página HTML de error, o un codec que el <audio>
   * no soporta: el intento siguiente salía del caché y no volvía a pedirle
   * nada a la fuente, así que la pista fallaba PARA SIEMPRE aunque el servidor
   * se hubiera recuperado.
   */
  private PendingCache: {
    key: string;

    blob: Blob;

    contentType?: string;
  } | null = null;

  constructor(
    AudioContext: AudioContext,

    destination: MediaStreamAudioDestinationNode,

    audioSource: string | Blob,
  ) {
    this.AudioContext = AudioContext;

    this.MediaStreamDestination = destination;

    this.AudioSource = audioSource;
  }

  /**
   * `true` = esta pista quedó sonando. `false` = no arrancó y NO está rota: la
   * instancia se descartó mientras bajaba o arrancaba, o el arranque lo
   * interrumpió un `pause()` o un `play()` más nuevo. `Playlist` distingue las
   * dos cosas: solo las excepciones son pistas que hay que saltear, un `false`
   * no se anuncia como reproducción ni dispara el salteo.
   *
   * El intento queda publicado en `PlayAttempt` mientras corre: es a lo que se
   * cuelga el `resumeTrack()` que llega mientras la pista todavía se baja, así
   * que el reanudar contesta con el resultado REAL del arranque —y no antes de
   * que exista el <audio>—.
   */
  async play(): Promise<boolean> {
    const attempt = this.startPlayback();

    this.PlayAttempt = attempt;

    try {
      return await attempt;
    } finally {
      // Solo el intento propio: un `play()` más nuevo ya publicó el suyo.

      if (this.PlayAttempt === attempt) this.PlayAttempt = null;
    }
  }

  /** El arranque de verdad. Lo envuelve `play()` para poder publicarlo. */
  private async startPlayback(): Promise<boolean> {
    if (this.Cancelled) return false;

    if (!this.AudioElement) {
      let src: string =
        typeof this.AudioSource === "string" ? this.AudioSource : "";

      try {
        if (typeof this.AudioSource !== "string") {
          this.ObjectUrl = URL.createObjectURL(this.AudioSource);

          src = this.ObjectUrl;
        } else {
          const cached = await cachedBlob(this.AudioSource);

          if (cached) {
            this.ObjectUrl = URL.createObjectURL(cached);

            src = this.ObjectUrl;
          } else {
            const workspacePrefix = "eriol-workspace://";

            if (this.AudioSource.startsWith(workspacePrefix)) {
              const path = this.AudioSource.slice(workspacePrefix.length);

              const parts = path.split("/");

              if (parts.length >= 5) {
                const dir = `/${parts[0]}/${parts[1]}/${parts[2]}/${parts[3]}/`;

                const file = parts.slice(4).join("/");

                const filesService = new FilesService("api");

                let blob = await filesService.readFile(dir, file);

                if (!blob || blob.size === 0) {
                  for (let i = 0; i < 3; i++) {
                    // Reintentar una lectura para una pista que ya nadie va a
                    // escuchar es un segundo entero de espera al pedo.

                    if (this.Cancelled) break;

                    await new Promise((r) => setTimeout(r, 300));

                    blob = await filesService.readFile(dir, file);

                    if (blob && blob.size > 0) break;
                  }
                }

                if (blob && blob.size > 0) {
                  const ct = audioTypeFromPath(file);

                  this.ObjectUrl = URL.createObjectURL(blob);

                  src = this.ObjectUrl;

                  // Se guarda recién cuando la reproducción lo valide.

                  this.PendingCache = {
                    key: this.AudioSource,

                    blob,

                    contentType: ct,
                  };
                }
              }
            } else if (REMOTE_SOURCE.test(this.AudioSource)) {
              // Camino fetch+cache de una fuente remota. #737 se llevó el proxy
              // local de la extensión (existía porque la galería tiraba de
              // hosts sin CORS, como ccMixter), no el camino: el `fetch`
              // directo sigue permitido por el `connect-src https:` de las dos
              // CSP (index.html y la del desktop en optimizedWindow.ts) y el
              // blob resultante se reproduce como `blob:`, que es lo único que
              // el `media-src` del desktop acepta. Si el host no manda CORS el
              // fetch tira y el guard de abajo convierte eso en un error
              // visible, en vez de un <audio> mudo.
              const downloaded = await this.fetchRemote(this.AudioSource);

              if (downloaded) {
                this.ObjectUrl = URL.createObjectURL(downloaded.blob);

                src = this.ObjectUrl;

                // Se guarda recién cuando la reproducción lo valide: una
                // respuesta 200 con bytes NO es una pista reproducible.

                this.PendingCache = {
                  key: this.AudioSource,

                  blob: downloaded.blob,

                  contentType: downloaded.contentType,
                };
              }
            }
          }
        }
      } catch {
        /* empty */
      }

      // Bajar la pista lleva tiempo, y en ese tiempo otra pista puede haber
      // pisado a ésta: `Playlist.play()` destruye el control anterior antes de
      // instalar el suyo. Seguir de acá para abajo con la instancia ya
      // descartada arrancaba un SEGUNDO <audio> encima del que está sonando, y
      // encima uno que nadie puede parar (el control vivo es otro). El blob
      // que llegó tarde se suelta acá mismo: `destroy()` no pudo revocarlo
      // porque todavía no existía.

      if (this.Cancelled) {
        // Todo lo que llegó tarde se suelta por el MISMO camino que lo suelta
        // un descarte normal: el blob —que `destroy()` no pudo revocar porque
        // todavía no existía— y los bytes que esperaban validación, que desde
        // que el cacheo se gana reproduciendo son un campo y no una variable
        // local, o sea hasta `REMOTE_MAX_BYTES` retenidos en el renderer que
        // compone la emisión por una pista que ya nadie va a escuchar.

        this.destroy();

        return false;
      }

      // Ni caché ni fetch dieron un blob: `src` sigue siendo la URL remota.

      // Dejarla caer a `new Audio(url_remota)` falla MUDO: el <audio>

      // cross-origin sin `crossOrigin` deja "tainted" al

      // MediaElementAudioSourceNode de más abajo y el grafo emite ceros — sin

      // error de media y sin audio en la emisión; en el desktop la CSP

      // (media-src sin https:) la corta con MEDIA_ERR code=4. Tirar acá hace

      // visible el problema y deja que `Playlist` salte a la pista siguiente

      // en vez de simular que suena.

      if (
        typeof this.AudioSource === "string" &&
        !this.ObjectUrl &&
        REMOTE_SOURCE.test(src)
      ) {
        // Genérico a propósito: `Playlist` loguea este error apenas lo
        // recibe, así que la URL entera (query firmada incluida) terminaría en
        // los logs. La pista se identifica acá, ya recortada.

        console.error(
          "❌ Fuente remota sin camino de reproducción soportado:",

          safeUrlForLog(src),
        );

        throw new Error("Fuente remota sin camino de reproducción soportado");
      }

      this.AudioElement = new Audio(src);

      this.AudioElement.loop = false;

      if (!this.SourceNode) {
        this.SourceNode = this.AudioContext.createMediaElementSource(
          this.AudioElement,
        );

        this.GainNode = this.AudioContext.createGain();

        this.AnalyserNode = this.AudioContext.createAnalyser();

        this.SourceNode.connect(this.GainNode);

        this.GainNode.connect(this.AnalyserNode);

        this.AnalyserNode.connect(this.AudioContext.destination);

        if (this.MediaStreamDestination) {
          this.AnalyserNode.connect(this.MediaStreamDestination);
        }
      }

      this.AudioElement.addEventListener("loadedmetadata", () => {
        if (
          this.AudioElement?.duration &&
          !Number.isNaN(this.AudioElement.duration)
        ) {
          this.Metadata.duration = this.AudioElement.duration;
        }
      });

      this.AudioElement.addEventListener("canplay", () => {
        if (
          this.AudioElement?.duration &&
          !Number.isNaN(this.AudioElement.duration)
        ) {
          this.Metadata.duration = this.AudioElement.duration;
        }

        // `canplay` = el browser decodificó lo suficiente como para sonar, que
        // es exactamente la prueba que le falta al blob para ir al caché. Vale
        // para el arranque que quedó en pausa (el del usuario, o el `play()`
        // más nuevo): ahí no hay un `play()` resuelto que lo valide.

        void this.commitCache();
      });

      this.AudioElement.addEventListener("durationchange", () => {
        if (
          this.AudioElement?.duration &&
          !Number.isNaN(this.AudioElement.duration)
        ) {
          this.Metadata.duration = this.AudioElement.duration;
        }
      });

      // `false` = el arranque no prendió; el <audio> puede seguir vivo igual.
      let startedNow = true;

      if (this.PauseRequested) {
        // El usuario pausó MIENTRAS se bajaba. El <audio> queda cargado y en
        // pausa (`new Audio()` nace pausado) y cableado por el camino de abajo:
        // `Playlist` no lo anuncia como sonando y el próximo `resume()` lo pone
        // a sonar. Arrancarlo acá era música saliendo al aire después de que el
        // usuario apretó "pausar".

        startedNow = false;
      } else {
        try {
          await this.AudioElement.play();
        } catch (error) {
          console.error("Error playing video:", error);

          // Que una respuesta traiga bytes no la hace reproducible: un HTML de
          // error o un codec que el <audio> no soporta rechazan acá con
          // `NotSupportedError` y, tragándoselo, `Playlist` emitía `Started`
          // por una pista muda que nunca dispara `ended` — rotación clavada.
          // Se propaga para que pueda saltear de verdad.

          // `AbortError` queda afuera: ahí el arranque lo interrumpió otra
          // cosa, no un defecto de la pista. Hay DOS productores en Chromium:
          // un `pause()` del usuario, y "The play() request was interrupted by
          // a new load request" — que es lo que dispara `destroy()` al poner
          // `AudioElement.src = ""`, y `Playlist.play()` hace exactamente eso
          // con el control anterior cuando entra una llamada concurrente
          // (doble clic en la lista, o un `nextTrack()` pisando la selección
          // del usuario).

          if ((error as DOMException | null)?.name !== "AbortError") {
            // Lo bajado NO va al caché. Y si el rechazo dice que estos BYTES
            // no se pueden reproducir, la copia que hubiera guardada también
            // se desaloja: si vino del caché (de una versión anterior, o del
            // precacheo de la lista) y no se decodifica, dejarla ahí es que la
            // pista falle para siempre, aunque el servidor se recupere.

            await this.dropCache(this.isUndecodable(error));

            this.destroy();

            throw error;
          }

          // Pero tampoco es un arranque: devolver como si nada hacía que
          // `Playlist` lo tomara por bueno, emitiera `Started` y colgara
          // `onTrackEnded` del control que esté instalado AHORA. Una pausa o un
          // cambio rápido de pista terminaban reportando como sonando una pista
          // que nunca arrancó.

          startedNow = false;
        }
      }

      // El `pause()` deja el <audio> CARGADO y en pausa: la pista no arrancó,
      // pero sigue acá y el próximo `resume()` la pone a sonar. Salir de una
      // con `return false` la dejaba SIN los listeners de abajo: sonaba hasta
      // el final sin disparar nunca `ended` —la rotación no volvía a avanzar,
      // que es aire muerto en emisión— y la barra de progreso se quedaba en 0.
      // El cableado va igual; que no arrancó lo dice el booleano.
      //
      // El otro productor de `AbortError` es `destroy()` (pone `src = ""`), y
      // ése SÍ deja `AudioElement` en null: seguir cableando listeners ahí
      // tiraba un TypeError desde adentro de `play()` que `Playlist` tomaba
      // como "pista rota". El `narrowing` de más arriba no lo ve porque no
      // sobrevive al `await`. Mirar el <audio> alcanza: `destroy()` es lo único
      // que lo pone en null, y lo hace siempre que existe.

      if (!this.AudioElement) return false;

      // Arrancó: recién ahora está probado que los bytes se decodifican, así
      // que recién ahora se guardan.

      if (startedNow) await this.commitCache();

      // `commitCache()` es OTRO await —escribe en el caché—, y el chequeo de
      // arriba pasó antes de él: un `destroy()` que aterriza justo ahí deja el
      // <audio> en null y cablear los listeners de abajo tiraba un TypeError
      // desde adentro de `play()`, que `Playlist` toma por "pista rota" y
      // saltea… una pista sana. Y `return startedNow` encima le contestaba
      // "está sonando" por una instancia ya descartada: el `true` por el que
      // `Playlist` necesita su guarda de identidad para no anunciar `Started`
      // de una pista que nadie está escuchando.

      if (this.Cancelled || !this.AudioElement) return false;

      this.AudioElement.addEventListener("error", async () => {
        const error = this.AudioElement?.error;

        if (error) {
          const dur = this.AudioElement?.duration ?? 0;

          if (Number.isFinite(dur) && dur > 0) {
            const nearEnd =
              (this.AudioElement?.currentTime ?? 0) >= Math.max(0, dur - 0.25);

            if (nearEnd) {
              try {
                this.AudioElement!.currentTime = Math.max(0, dur - 1);

                if (this.AudioElement!.paused === false) {
                  await this.AudioElement!.play();
                }
              } catch (error) {
                console.error("Error playing video:", error);
              }
            }
          }

          switch (error.code) {
            case error.MEDIA_ERR_ABORTED:
              console.error("❌ Reproducción abortada por el usuario.");

              break;

            case error.MEDIA_ERR_NETWORK:
              console.error("❌ Error de red durante la descarga.");

              break;

            case error.MEDIA_ERR_DECODE:
              console.error("❌ Error decodificando audio.");

              break;

            case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
              console.error("❌ El formato o codec no es soportado.");

              break;

            default:
              console.error("❌ Error desconocido cargando audio.");
          }
        }
      });

      this.AudioElement.addEventListener("timeupdate", () => {
        if (
          this.AudioElement instanceof HTMLAudioElement &&
          !this.AudioElement.paused
        ) {
          PlaylistEventsManager.emit("track.timeupdate", {
            currentTime: this.AudioElement.currentTime,

            duration: this.AudioElement.duration,
          });
        }
      });

      return startedNow;
    } else if (this.AudioElement.paused) {
      try {
        await this.AudioElement.play();
      } catch (error) {
        console.error("Error playing video:", error);

        return false;
      }

      return !this.Cancelled;
    }

    // Ya venía sonando.

    return true;
  }

  /**
   * Bajada remota ENTERA —headers y cuerpo— atada a la vida de la instancia:
   * el `AbortController` es el MISMO que corta `destroy()`, así que una pista
   * descartada no sigue ocupando la red ni vuelve más tarde con bytes para
   * arrancar audio por su cuenta.
   *
   * El tope de tiempo tiene que cubrir la bajada COMPLETA, y por eso el cuerpo
   * se lee acá adentro: `fetch` resuelve apenas llegan los headers y el grueso
   * de los bytes viene después, en `response.blob()`. Soltar ahí el tope —y el
   * `RemoteFetch` que mira `destroy()`— dejaba sin tope y sin cancelación justo
   * el caso para el que se puso: un host que acepta la conexión y no manda el
   * cuerpo. Eso cuelga `play()` para siempre, y `play()` es parte de la cadena
   * de rotación y de la acción `playlist_play` de los disparadores —que corre
   * con el candado del motor—: aire muerto en emisión y disparadores mudos.
   *
   * `null` = no hubo bytes reproducibles (respuesta de error, cuerpo vacío o
   * una respuesta que se pasa del tope de tamaño).
   */
  private async fetchRemote(
    url: string,
  ): Promise<{ blob: Blob; contentType?: string } | null> {
    const controller = new AbortController();

    this.RemoteFetch = controller;

    // La instancia pudo quedar descartada mientras se leía el caché: la
    // bajada sale abortada de entrada, en vez de ocupar la red para una pista
    // que ya nadie va a escuchar.

    if (this.Cancelled) controller.abort();

    let timeout: ReturnType<typeof setTimeout> | null = null;

    // Rearmar = "seguís vivo, te doy otros 20 s". Se llama con cada chunk del
    // cuerpo, así que lo que vence el tope es el SILENCIO del host, no lo que
    // tarde una pista grande en un enlace lento.

    const arm = () => {
      if (timeout) clearTimeout(timeout);

      timeout = setTimeout(() => controller.abort(), REMOTE_FETCH_TIMEOUT_MS);
    };

    arm();

    try {
      const response = await fetch(url, {
        credentials: "omit",

        signal: controller.signal,
      });

      if (!response.ok) return null;

      // Sin `Content-Type` del host, la extensión de la URL. La fuente de un
      // `blob:` es el `type` del Blob —no hay headers que mirar—, así que un
      // blob con `type` vacío es una pista sana que el <audio> puede rechazar
      // con "formato no soportado", y ese rechazo hoy además DESALOJA la copia
      // del caché: bajarla de nuevo en cada vuelta de la rotación, para
      // siempre.

      const contentType =
        response.headers.get("Content-Type") || audioTypeFromPath(url);

      // Lo que el host ya declara como enorme ni se baja: un `Content-Length`
      // pasado de tope se corta antes del primer byte de cuerpo.

      const declared = Number(response.headers.get("Content-Length"));

      if (Number.isFinite(declared) && declared > REMOTE_MAX_BYTES) {
        controller.abort();

        this.logTooBig(url, declared);

        return null;
      }

      const blob = await this.readCapped(
        response,

        url,

        controller,

        contentType,

        arm,
      );

      if (!blob || blob.size === 0) return null;

      return {
        blob,

        contentType: contentType || blob.type || undefined,
      };
    } finally {
      if (timeout) clearTimeout(timeout);

      timeout = null;

      if (this.RemoteFetch === controller) this.RemoteFetch = null;
    }
  }

  /**
   * Lee el cuerpo MIENTRAS llega y corta apenas se pasa de `REMOTE_MAX_BYTES`.
   * Juntarlo entero con `response.blob()` y recién después mirar el tamaño no
   * protege de nada: para cuando se puede medir, los bytes ya están en la
   * memoria del renderer (y un cuerpo sin fin ni siquiera llega a ese punto).
   * El corte aborta la bajada de verdad, no solo suelta el resultado.
   *
   * `null` = se pasó del tope.
   */
  private async readCapped(
    response: Response,

    url: string,

    controller: AbortController,

    contentType: string | undefined,

    /** Rearma el tope de inactividad: lo llama cada chunk que llega. */
    keepAlive: () => void,
  ): Promise<Blob | null> {
    const body = response.body;

    if (!body || typeof body.getReader !== "function") {
      // Sin cuerpo en streaming (un entorno sin `ReadableStream`): el tope se
      // aplica sobre lo ya juntado. Protege el caché y la reproducción, pero
      // no la memoria: es el camino de último recurso, no el de producción —y
      // por eso las pruebas de la bajada remota corren por el de arriba.

      const whole = await response.blob();

      if (whole.size > REMOTE_MAX_BYTES) {
        controller.abort();

        this.logTooBig(url, whole.size);

        return null;
      }

      return whole;
    }

    const reader = body.getReader();

    const chunks: BlobPart[] = [];

    let total = 0;

    for (;;) {
      const { done, value } = await reader.read();

      if (done) break;

      keepAlive();

      if (!value) continue;

      total += value.byteLength;

      if (total > REMOTE_MAX_BYTES) {
        // Cortar la bajada, no solo dejar de leerla: el host sigue mandando
        // bytes hasta que el `fetch` se aborta.

        controller.abort();

        this.logTooBig(url, total);

        return null;
      }

      chunks.push(value);
    }

    return new Blob(chunks, contentType ? { type: contentType } : undefined);
  }

  /** Un solo lugar para el aviso, con la URL ya recortada. */
  private logTooBig(url: string, bytes: number) {
    console.error(
      "❌ Pista remota por encima del tope de tamaño; se descarta:",

      safeUrlForLog(url),

      `${Math.round(bytes / (1024 * 1024))} MiB >= ${Math.round(
        REMOTE_MAX_BYTES / (1024 * 1024),
      )} MiB`,
    );
  }

  /**
   * Guarda en el caché lo que se bajó, ahora que la reproducción probó que se
   * puede decodificar. Idempotente: lo llaman el arranque resuelto y el
   * `canplay`, y el primero que gane deja al otro sin nada que hacer.
   */
  private async commitCache() {
    const pending = this.PendingCache;

    if (!pending) return;

    this.PendingCache = null;

    await cacheBestEffort(pending.key, pending.blob, pending.contentType);
  }

  /**
   * La pista no se pudo reproducir: lo bajado no se guarda nunca, y con
   * `evict` también se saca del caché la copia que hubiera, así el intento
   * siguiente vuelve a la fuente en vez de repetir para siempre el mismo blob
   * que no se decodifica.
   */
  private async dropCache(evict: boolean) {
    this.PendingCache = null;

    if (!evict) return;

    if (typeof this.AudioSource !== "string") return;

    await evictBestEffort(this.AudioSource);
  }

  /**
   * `true` solo cuando el rechazo dice que ESTOS bytes no son reproducibles.
   * La política de autoplay rechaza con `NotAllowedError` y la pista puede
   * estar perfecta: desalojarla ahí sería tirar una copia sana y volver a
   * bajarla en el próximo intento.
   */
  private isUndecodable(error: unknown): boolean {
    if ((error as DOMException | null)?.name === "NotSupportedError") {
      return true;
    }

    const mediaError = this.AudioElement?.error;

    if (!mediaError) return false;

    return (
      mediaError.code === mediaError.MEDIA_ERR_DECODE ||
      mediaError.code === mediaError.MEDIA_ERR_SRC_NOT_SUPPORTED
    );
  }

  /** Suelta el blob, exista de antes o haya llegado tarde. */
  private releaseObjectUrl() {
    if (!this.ObjectUrl) return;

    URL.revokeObjectURL(this.ObjectUrl);

    this.ObjectUrl = null;
  }

  isPaused() {
    if (this.AudioElement) return this.AudioElement.paused;

    // Sin <audio> pero con la pausa ya pedida, la pista SÍ está en pausa: se
    // está bajando y el arranque pendiente la va a dejar cargada y muda.
    // Devolver `undefined` acá dejaba ese estado invisible para todos sus
    // consumidores: el botón Play no reanudaba, caía en `play(0)` y la lista
    // ARRANCABA DE CERO —matando de paso la bajada en vuelo—, y el disparador
    // `playlist_play` encima rehacía el precacheo entero con el candado del
    // motor tomado.

    if (this.PauseRequested && !this.Cancelled) return true;

    return undefined;
  }

  updateVolume(volume: number) {
    if (!this.GainNode) {
      return;
    }

    this.GainNode.gain.setValueAtTime(volume, this.AudioContext.currentTime);

    if (this.AudioElement) {
      this.AudioElement.volume = volume;
    }
  }

  getVolume(): number | null {
    // `null`, no 0: sin <audio> la pista nunca sonó y no tiene volumen que

    // heredar. Devolver 0 hacía que `Playlist.play` arrancara la pista

    // SIGUIENTE en silencio.

    if (!this.AudioElement) return null;

    return this.AudioElement.volume;
  }

  rewind(seconds: number) {
    if (!this.AudioElement) return;

    this.AudioElement.currentTime = Math.max(
      0,
      this.AudioElement.currentTime - seconds,
    );
  }

  forward(seconds: number) {
    if (!this.AudioElement) return;

    this.AudioElement.currentTime = Math.min(
      this.Metadata.duration || 0,

      this.AudioElement.currentTime + seconds,
    );
  }

  seekTo(newTime: number) {
    if (!this.AudioElement) return;

    const dur = this.AudioElement.duration;

    if (Number.isFinite(dur) && !Number.isNaN(dur) && dur > 0) {
      const safeTime = Math.min(Math.max(0, newTime), Math.max(0, dur - 0.5));

      this.AudioElement.currentTime = safeTime;
    } else {
      this.AudioElement.currentTime = Math.max(0, newTime);
    }
  }

  pauseTrack() {
    if (!this.AudioElement) {
      // Todavía no hay <audio>: la pista se está bajando. Volver en seco de acá
      // era la pausa que no pausaba nada — la bajada seguía viva y su
      // continuación creaba y arrancaba el audio DESPUÉS de la pausa. Queda
      // marcado para que ese arranque pendiente deje la pista cargada y muda.

      this.PauseRequested = true;

      return;
    }

    this.AudioElement.pause();
  }

  /**
   * `true` solo si la pista VOLVIÓ A SONAR. El transporte se deriva de eso, así
   * que el reanudar de una pista que todavía se está bajando no puede
   * contestar por adelantado: se cuelga del arranque en vuelo y devuelve lo que
   * ése resuelva.
   *
   * Tira cuando el reanudar prueba que los bytes NO se decodifican, igual que
   * `play()`: una pista que quedó cargada y en pausa nunca pasó por el `play()`
   * del <audio>, así que su primer reanudar es la primera prueba real de que se
   * puede reproducir. Tragarse ese rechazo dejaba la rotación estacionada en
   * una pista que no suena ni dispara `ended`.
   */
  async resumeTrack(): Promise<boolean> {
    if (!this.AudioElement) {
      // Todavía no hay <audio>: la pista se está bajando. Si el usuario había
      // pedido pausa, ESTO es levantarla —lo que pidió es que suene— y el
      // arranque en vuelo la pone a sonar apenas termine de bajar. Volver en
      // seco de acá descartaba el Play y la pista aterrizaba muda para
      // siempre, sin que nadie hubiera vuelto a pedir pausa.

      if (this.PauseRequested && !this.Cancelled) {
        const attempt = this.PlayAttempt;

        // Sin arranque en vuelo no hay nada que esperar ni nada que levantar:
        // la pausa se pidió sobre una pista que nunca llegó a bajarse.
        //
        // La guarda va ANTES de consumir el latch a propósito. Contestar "no
        // sonó" habiéndolo limpiado igual borraba el único estado que hace
        // visible esa pausa: `isPaused()` se caía a `undefined` y con eso el
        // botón Play deja de reanudar y cae en `play(0)` —la lista arranca de
        // cero—, que es justo lo que el latch vino a evitar.

        if (!attempt) return false;

        this.PauseRequested = false;

        // Devolver `true` acá era anunciar el arranque ANTES de decodificar:
        // el <audio> ni existe. La UI se quedaba diciendo "reproduciendo"
        // mientras la bajada seguía —y hasta si terminaba mal—, y cuando
        // terminaba BIEN el arranque se anunciaba DE NUEVO, ahora sí desde el
        // `play()`. El resultado de este reanudar es el del intento original.

        // El fallo, en cambio, es del intento original y de nadie más:
        // `Playlist` ya lo recibe por el `play()` que está esperando y saltea
        // la pista ahí. Propagarlo también acá la salteaba DOS veces —la
        // rotación se corría de más y la racha de irreproducibles contaba
        // doble—, así que para el reanudar eso es, simplemente, "no sonó".

        return await attempt.catch(() => false);
      }

      // Nunca hubo pista cargada: no hay nada que reanudar y darlo por bueno
      // dejaba el botón en "pausar" con silencio.

      return false;
    }

    this.PauseRequested = false;

    try {
      await this.AudioElement.play();
    } catch (error) {
      console.error("Error playing video:", error);

      if (
        (error as DOMException | null)?.name !== "AbortError" &&
        this.isUndecodable(error)
      ) {
        await this.dropCache(true);

        this.destroy();

        throw error;
      }

      return false;
    }

    return !this.Cancelled;
  }

  destroy() {
    this.Cancelled = true;

    // Cortar la bajada en vuelo. Sin esto el `fetch` seguía hasta el final y
    // esta instancia —ya reemplazada— retomaba después del `await` con los
    // bytes en la mano.

    this.RemoteFetch?.abort();

    this.RemoteFetch = null;

    // Los bytes que esperaban validación no son una variable local de `play()`
    // desde que el cacheo se gana reproduciendo: son un campo, y una pista
    // descartada a mitad de vuelo los dejaba vivos —hasta `REMOTE_MAX_BYTES`—
    // en el mismo renderer que ese tope vino a proteger.

    this.PendingCache = null;

    if (this.AudioElement) {
      this.AudioElement.pause();

      this.AudioElement.src = "";

      this.AudioElement = null;
    }

    this.releaseObjectUrl();

    if (this.SourceNode) {
      this.SourceNode.disconnect();

      this.SourceNode = null;
    }

    if (this.GainNode) {
      this.GainNode.disconnect();

      this.GainNode = null;
    }

    if (this.AnalyserNode) {
      this.AnalyserNode.disconnect();

      this.AnalyserNode = null;
    }

    this.Metadata = {};
  }

  getAnalyser(): AnalyserNode | null {
    return this.AnalyserNode;
  }

  onTrackEnded(callback: () => void) {
    if (this.AudioElement) {
      this.AudioElement.addEventListener("ended", callback);
    }
  }
}

export default PlaylistTrack;
